import { useEffect } from "react";

/* GSAP orchestration, loaded client-side only (prerender never runs effects).
   Declarative hooks via data attributes:
     [data-reveal]        — fade-rise when scrolled into view
     [data-reveal-group]  — stagger-reveal direct children
     [data-presence]      — animate a presence bar's width to N%, then drift
     [data-word-reveal]   — per-word stagger (children are word spans)
     #site-nav            — .is-scrolled toggled past 24px (works regardless
                            of reduced-motion: it's state, not decoration)
     #hero-art            — settle-in scale on load, then a slow drift
     #hero-copy           — staggered entrance on load
     #hero-breath         — slow breathing lamplight vignette
   All *animations* are skipped under prefers-reduced-motion. */
export function SiteMotion() {
  useEffect(() => {
    const nav = document.getElementById("site-nav");
    const onScroll = () =>
      nav?.classList.toggle("is-scrolled", window.scrollY > 24);
    onScroll();
    addEventListener("scroll", onScroll, { passive: true });

    let ctx: { revert(): void } | undefined;
    let cancelled = false;

    if (!matchMedia("(prefers-reduced-motion: reduce)").matches) {
      (async () => {
        const { gsap } = await import("gsap");
        const { ScrollTrigger } = await import("gsap/ScrollTrigger");
        if (cancelled) return;
        gsap.registerPlugin(ScrollTrigger);

        ctx = gsap.context(() => {
          gsap
            .timeline()
            .fromTo(
              "#hero-art",
              { scale: 1.06 },
              { scale: 1, duration: 2.4, ease: "power2.out" },
            )
            // keep the den alive: a slow, near-imperceptible drift
            .to("#hero-art", {
              scale: 1.03,
              duration: 16,
              ease: "sine.inOut",
              repeat: -1,
              yoyo: true,
            });
          gsap.fromTo(
            "#hero-copy > *",
            { y: 40, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.9, stagger: 0.12, ease: "power3.out", delay: 0.2 },
          );
          gsap.to("#hero-breath", {
            opacity: 0.75,
            duration: 3.6,
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true,
          });

          for (const el of gsap.utils.toArray<HTMLElement>("[data-reveal]")) {
            gsap.fromTo(
              el,
              { y: 28, opacity: 0 },
              {
                y: 0, opacity: 1, duration: 0.8, ease: "power2.out",
                scrollTrigger: { trigger: el, start: "top 82%" },
              },
            );
          }
          for (const group of gsap.utils.toArray<HTMLElement>("[data-reveal-group]")) {
            gsap.fromTo(
              group.children,
              { y: 28, opacity: 0 },
              {
                y: 0, opacity: 1, duration: 0.7, stagger: 0.12, ease: "power2.out",
                scrollTrigger: { trigger: group, start: "top 80%" },
              },
            );
          }
          gsap.utils.toArray<HTMLElement>("[data-presence]").forEach((bar, i) => {
            const target = Number(bar.dataset.presence ?? 50);
            gsap.fromTo(
              bar,
              { width: "0%" },
              {
                width: `${target}%`,
                duration: 1.1,
                delay: i * 0.15,
                ease: "power2.out",
                scrollTrigger: { trigger: bar, start: "top 90%" },
                // after settling, the mix stays faintly alive
                onComplete: () => {
                  gsap.to(bar, {
                    width: `${target + 3}%`,
                    duration: 6,
                    ease: "sine.inOut",
                    repeat: -1,
                    yoyo: true,
                  });
                },
              },
            );
          });
          for (const line of gsap.utils.toArray<HTMLElement>("[data-word-reveal]")) {
            gsap.fromTo(
              line.querySelectorAll("span"),
              { opacity: 0.12 },
              {
                opacity: 1, stagger: 0.06, ease: "none",
                scrollTrigger: { trigger: line, start: "top 75%", end: "top 40%", scrub: true },
              },
            );
          }
        });
      })();
    }

    return () => {
      cancelled = true;
      removeEventListener("scroll", onScroll);
      ctx?.revert();
    };
  }, []);
  return null;
}
